import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  UploadCloud,
  FileText,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Sliders,
  FileCheck,
  AlertTriangle,
  Layers,
  FileCode,
  Image,
} from 'lucide-react';
import { Header } from '../components/Header.js';
import { EduCard } from '../components/EduCard.js';
import { EduButton } from '../components/EduButton.js';
import { api } from '../services/api.js';
import { Project, LessonSceneDefaults, DEFAULT_LESSON_SCENE_DEFAULTS } from '../types/index.js';

export const NewLesson: React.FC = () => {
  const [searchParams] = useSearchParams();
  const preselectedProjectId = searchParams.get('projectId') || '';

  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState(preselectedProjectId);
  const [lessonTitle, setLessonTitle] = useState('');

  // Material Files
  const [visualFile, setVisualFile] = useState<File | null>(null);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [scriptFile, setScriptFile] = useState<File | null>(null);
  const [scriptText, setScriptText] = useState('');

  // Defaults
  const [sceneDefaults, setSceneDefaults] = useState<LessonSceneDefaults>({
    ...DEFAULT_LESSON_SCENE_DEFAULTS,
  });

  const [loadingProjects, setLoadingProjects] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    async function load() {
      try {
        const res = await api.getProjects();
        setProjects(res.items || []);
        if (res.items && res.items.length > 0 && !selectedProjectId) {
          setSelectedProjectId(res.items[0].id);
        }
      } finally {
        setLoadingProjects(false);
      }
    }
    load();
  }, [selectedProjectId]);

  // Handle Visual Material (PDF, ZIP, or Images)
  const handleVisualFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      if (e.target.files.length === 1) {
        const file = e.target.files[0];
        setVisualFile(file);
        setImageFiles([]);
        if (!lessonTitle) {
          setLessonTitle(file.name.replace(/\.[^/.]+$/, '').replace(/_/g, ' '));
        }
      } else {
        // Multiple images
        const filesArray = Array.from(e.target.files);
        setImageFiles(filesArray);
        setVisualFile(null);
        if (!lessonTitle) {
          setLessonTitle('Videoaula Importada');
        }
      }
    }
  };

  // Handle Script Material (Markdown, TXT, CSV)
  const handleScriptFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setScriptFile(file);
      const text = await file.text();
      setScriptText(text);

      // Auto-extract title from markdown if available
      const firstHeading = text.match(/^#\s*(.+)$/m);
      if (firstHeading && firstHeading[1] && !lessonTitle) {
        setLessonTitle(firstHeading[1].trim());
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProjectId) {
      alert('Selecione um projeto para a aula.');
      return;
    }
    if (!lessonTitle.trim()) {
      alert('Informe o título da aula.');
      return;
    }
    if (!visualFile && imageFiles.length === 0 && !scriptText.trim()) {
      alert('Por favor, envie um arquivo de slides (PDF ou imagens) e/ou o arquivo de roteiro.');
      return;
    }

    setSubmitting(true);
    setFeedbackMessage(null);

    try {
      const formData = new FormData();
      formData.append('projectId', selectedProjectId);
      formData.append('lessonTitle', lessonTitle.trim());
      formData.append('sceneDefaults', JSON.stringify(sceneDefaults));

      if (visualFile) {
        formData.append('visualFile', visualFile, visualFile.name);
      } else if (imageFiles.length > 0) {
        for (const img of imageFiles) {
          formData.append('images', img, img.name);
        }
      }

      if (scriptFile) {
        formData.append('scriptFile', scriptFile, scriptFile.name);
      } else if (scriptText.trim()) {
        formData.append('scriptText', scriptText);
      }

      const res = await api.importLesson(formData);

      setFeedbackMessage(
        res.message || 'Aula importada com sucesso. Revise e configure suas cenas antes de iniciar a produção.'
      );

      // Redirect automatically to dedicated Carousel Editor
      setTimeout(() => {
        navigate(`/aulas/${res.lesson.id}/editor`);
      }, 900);
    } catch (err: any) {
      alert(err.message || 'Falha ao importar e processar materiais.');
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Header
        title="Nova Videoaula"
        subtitle="Importe materiais (PDF ou imagens + roteiro) para processamento e geração automatizada"
      />

      <div className="p-8 max-w-4xl mx-auto space-y-6">
        {feedbackMessage && (
          <div className="p-4 rounded-xl bg-status-success/10 border border-status-success/30 text-status-success flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
            <div>
              <p className="text-sm font-bold">{feedbackMessage}</p>
              <p className="text-xs opacity-90 mt-0.5">Abrindo o Editor Visual de Cenas...</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Step 1: Projeto e Título */}
          <EduCard title="1. Projeto e Dados da Aula">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-txt-primary uppercase mb-1.5">
                  Projeto <span className="text-brand-primary">*</span>
                </label>
                <select
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  disabled={loadingProjects}
                  required
                  className="w-full text-sm p-3 rounded-lg border border-bdr bg-white focus:border-brand-primary outline-none"
                >
                  <option value="" disabled>
                    Selecione um projeto...
                  </option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.lessonCount} aulas)
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-txt-primary uppercase mb-1.5">
                  Título da Videoaula <span className="text-brand-primary">*</span>
                </label>
                <input
                  type="text"
                  value={lessonTitle}
                  onChange={(e) => setLessonTitle(e.target.value)}
                  placeholder="Ex: Regime Jurídico Administrativo — Fundamentos Iniciais"
                  required
                  className="w-full text-sm p-3 rounded-lg border border-bdr bg-white focus:border-brand-primary outline-none"
                />
              </div>
            </div>
          </EduCard>

          {/* Step 2: Materiais Visuais (PDF ou Imagens) */}
          <EduCard title="2. Slides e Material Visual (PDF ou Imagens)">
            <div className="space-y-4">
              <p className="text-xs text-txt-secondary">
                Envie o arquivo <strong>PDF da apresentação</strong> (o sistema separa automaticamente cada página em imagem de alta resolução) ou um pacote <strong>ZIP com imagens já preparadas</strong>.
              </p>

              <label className="block border-2 border-dashed border-bdr hover:border-brand-primary/60 rounded-xl p-6 text-center cursor-pointer transition-colors bg-surface-secondary/40 hover:bg-brand-light/30">
                <input
                  type="file"
                  accept=".pdf,.zip,image/png,image/jpeg,image/webp"
                  multiple
                  onChange={handleVisualFileChange}
                  className="hidden"
                />
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 rounded-full bg-brand-light text-brand-primary flex items-center justify-center">
                    <UploadCloud className="w-6 h-6" />
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-txt-primary">
                      {visualFile
                        ? visualFile.name
                        : imageFiles.length > 0
                        ? `${imageFiles.length} imagens selecionadas`
                        : 'Clique para selecionar o PDF ou Imagens'}
                    </span>
                    <p className="text-xs text-txt-muted mt-0.5">
                      Suporta PDF, ZIP ou seleção de múltiplos arquivos PNG/JPG
                    </p>
                  </div>
                </div>
              </label>
            </div>
          </EduCard>

          {/* Step 3: Roteiro de Narração */}
          <EduCard title="3. Arquivo de Roteiro (Markdown, TXT ou CSV)">
            <div className="space-y-4">
              <p className="text-xs text-txt-secondary">
                Envie o arquivo contendo a identificação das cenas/slides e o texto da narração (formato Markdown, bloco de texto ou CSV com <code>## Slide 01</code> e <code>**Narração:**</code>).
              </p>

              <div className="flex items-center gap-3">
                <label className="flex-1 border-2 border-dashed border-bdr hover:border-brand-primary/60 rounded-xl p-4 text-center cursor-pointer transition-colors bg-surface-secondary/40 hover:bg-brand-light/30">
                  <input
                    type="file"
                    accept=".md,.txt,.csv"
                    onChange={handleScriptFileChange}
                    className="hidden"
                  />
                  <div className="flex items-center justify-center gap-2 text-txt-primary">
                    <FileText className="w-5 h-5 text-brand-primary" />
                    <span className="text-xs font-semibold">
                      {scriptFile ? scriptFile.name : 'Selecionar arquivo de roteiro (.md, .txt, .csv)'}
                    </span>
                  </div>
                </label>
              </div>

              <div>
                <label className="block text-xs font-semibold text-txt-primary uppercase mb-1">
                  Ou cole o texto do roteiro diretamente:
                </label>
                <textarea
                  rows={5}
                  value={scriptText}
                  onChange={(e) => setScriptText(e.target.value)}
                  placeholder={`Exemplo:\n## Slide 01 — Introdução\n**Narração:**\nOlá! Sejam bem-vindos à nossa aula sobre os fundamentos...\n\n## Slide 02 — Conceitos Gerais\n**Narração:**\nNeste ponto, analisaremos os princípios estruturantes...`}
                  className="w-full text-xs font-mono p-3 rounded-lg border border-bdr bg-white focus:border-brand-primary outline-none"
                />
              </div>
            </div>
          </EduCard>

          {/* Step 4: Configurações Padrão Iniciais */}
          <EduCard title="4. Configurações Padrão da Aula (Herdadas pelas Cenas)">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">Voz Inicial</label>
                <select
                  value={sceneDefaults.voiceId || 'faber'}
                  onChange={(e) => setSceneDefaults({ ...sceneDefaults, voiceId: e.target.value })}
                  className="w-full text-xs p-2.5 rounded-lg border border-bdr bg-white focus:border-brand-primary outline-none"
                >
                  <option value="faber">Faber (Voz Oficial EduRender - Português BR)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">Velocidade</label>
                <select
                  value={sceneDefaults.speechRate}
                  onChange={(e) =>
                    setSceneDefaults({ ...sceneDefaults, speechRate: parseFloat(e.target.value) })
                  }
                  className="w-full text-xs p-2.5 rounded-lg border border-bdr bg-white focus:border-brand-primary outline-none"
                >
                  <option value="0.9">0.9x (Pausada)</option>
                  <option value="1.0">1.0x (Padrão natural)</option>
                  <option value="1.1">1.1x (Dinâmica)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Pausa Inicial Padrão (s)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="10"
                  value={sceneDefaults.startDelaySeconds}
                  onChange={(e) =>
                    setSceneDefaults({
                      ...sceneDefaults,
                      startDelaySeconds: parseFloat(e.target.value) || 0,
                    })
                  }
                  className="w-full text-xs p-2.5 rounded-lg border border-bdr bg-white focus:border-brand-primary outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Pausa Final Padrão (s)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="10"
                  value={sceneDefaults.endDelaySeconds}
                  onChange={(e) =>
                    setSceneDefaults({
                      ...sceneDefaults,
                      endDelaySeconds: parseFloat(e.target.value) || 0,
                    })
                  }
                  className="w-full text-xs p-2.5 rounded-lg border border-bdr bg-white focus:border-brand-primary outline-none"
                />
              </div>
            </div>
          </EduCard>

          {/* Submit Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <EduButton
              type="button"
              variant="secondary"
              onClick={() => navigate('/aulas')}
              disabled={submitting}
            >
              Cancelar
            </EduButton>

            <EduButton
              type="submit"
              variant="primary"
              size="lg"
              icon={<ArrowRight size={16} />}
              isLoading={submitting}
            >
              Importar Materiais e Abrir Editor
            </EduButton>
          </div>
        </form>
      </div>
    </div>
  );
};
